import React, { useContext, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import useSiteSettings from "@/hooks/useSiteSettings";
import { AppContext } from "../../../context/AppContext";
import { ReactComponent as PORTFOLIO_IMG } from "../../../assets/images/Frame (13).svg";
import Modal from "../Modal";
import WalletHome from "./WalletHome";
import WalletPanel from "./WalletPanel";
import { StyledCashierModal } from "./styles";
import { requestWalletSummarySync } from "../../../utils/walletSync";

const WalletHeaderIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
    <path
      d="M6.4 7.2h15.2c1.6 0 2.9 1.3 2.9 2.9v11.3c0 1.6-1.3 2.9-2.9 2.9H6.4c-1.6 0-2.9-1.3-2.9-2.9V10.1c0-1.6 1.3-2.9 2.9-2.9Z"
      fill="currentColor"
      opacity=".9"
    />
    <path
      d="M8.6 5.1h10.8c.9 0 1.6.7 1.6 1.6v2.1H7V6.7c0-.9.7-1.6 1.6-1.6Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path d="M21 14.2h3.5v4.4H21a2.2 2.2 0 1 1 0-4.4Z" fill="#101926" opacity=".85" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M6.2 6.2 12 12m0 0 5.8 5.8M12 12l5.8-5.8M12 12l-5.8 5.8"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2.4"
    />
  </svg>
);

const CashierModal = ({
  button,
  triggerClassName = "",
  hideTrigger = false,
  isOpen: controlledIsOpen,
  defaultView = null,
  onOpenChange,
}) => {
  const { isTabletScreen } = useContext(AppContext);
  const { user } = useAuth();
  const { settings } = useSiteSettings();
  const isControlled = typeof controlledIsOpen === "boolean";
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [activeView, setActiveView] = useState(defaultView || null);
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const authenticated = user?.isAuthenticated === true;
  const profileKey = String(user?.profile?.id || user?.profile?.user_id || user?.profile?.member_id || user?.profile?.user_unique_id || "");
  const buttonLabel = button || "지갑";

  useEffect(() => {
    if (isOpen) setActiveView(defaultView || null);
  }, [defaultView, isOpen]);

  const updateOpenState = (nextIsOpen) => {
    if (isControlled) {
      onOpenChange?.(nextIsOpen);
      return;
    }
    setInternalIsOpen(nextIsOpen);
  };

  const handleOpenModal = () => {
    setActiveView(defaultView || null);
    updateOpenState(true);
  };

  const handleCloseModal = () => {
    updateOpenState(false);
  };

  const renderTrigger = () => {
    if (triggerClassName) {
      return (
        <button type="button" className={triggerClassName} onClick={handleOpenModal}>
          {buttonLabel}
        </button>
      );
    }

    if (isTabletScreen) {
      return (
        <button className="button" type="button" onClick={handleOpenModal} aria-label={buttonLabel}>
          <PORTFOLIO_IMG />
        </button>
      );
    }

    return (
      <button
        type="button"
        className="cashier-trigger-button"
        onClick={handleOpenModal}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 40,
          padding: "0 16px",
          border: 0,
          borderRadius: 10,
          color: "#06110b",
          background: "linear-gradient(180deg, #59f477, #25d963)",
          fontWeight: 900,
          cursor: "pointer",
        }}
      >
        {buttonLabel}
      </button>
    );
  };

  return (
    <>
      {hideTrigger ? null : renderTrigger()}

      <Modal
        maxWidth={760}
        isOpen={isOpen}
        onClose={handleCloseModal}
        panelClassName="wallet-dialog-panel"
      >
        <StyledCashierModal>
          <header className="cashier-modal-header">
            <div className="cashier-modal-title">
              <WalletHeaderIcon />
              <span>지갑</span>
            </div>
            <button type="button" className="cashier-modal-close" onClick={handleCloseModal}>
              <CloseIcon />
            </button>
          </header>

          <div className={`cashier-modal-body ${activeView ? "is-detail" : "is-home"}`}>
            {!authenticated ? <p role="status">로그인 후 입출금 정보를 확인해주세요.</p> : activeView ? (
              <WalletPanel
                key={`${user?.authSessionRevision ?? "session"}:${profileKey}`}
                authenticated={authenticated}
                view={activeView}
                profile={user?.profile}
                settings={settings}
                onBack={() => setActiveView(null)}
                onRefreshWallet={() => requestWalletSummarySync("cashier-action-complete")}
              />
            ) : (
              <WalletHome profile={user?.profile} onSelect={setActiveView} />
            )}
          </div>
        </StyledCashierModal>
      </Modal>
    </>
  );
};

export default CashierModal;
